<script>
  import { format } from 'svelte-i18n';
  import { Button } from '@sveltestrap/sveltestrap';

  import { Field, FieldActions, FieldHeader, FieldLabel, FieldValue } from '@client/components/fields/standalone/base';
  import { Icon } from '@client/components/icon';
  import { Input } from '@client/components/input';

  import { deepCopy } from '@common/utils/objects';

  export let id = null;
  export let label = null;
  export let values = [];
  export let emptyPlaceholder = '-';

  export let formatKey = (val) => val ?? emptyPlaceholder;
  export let formatValue = (val) => val ?? emptyPlaceholder;

  export let addButtonLabel = $format('label.ADD');
  export let hideActionButtons = false;

  export let validation = undefined;
  export let validationError = undefined;

  export let keyColumnLabel = $format('label.KEY');
  export let keyProperty = 'name';
  export let keyReadOnly = false;
  export let keyValidation = validation;
  export let keyValidationError = validationError;

  export let valueColumnLabel = $format('label.VALUE');
  export let valueProperty = 'value';
  export let valueReadOnly = false;
  export let valueValidation = validation;
  export let valueValidationError = validationError;

  export let tooltip = undefined;
  export let tooltipIcon = undefined;

  export let readonly = false;
  export let editing = false;

  export let showActions = true;

  export let onUpdate = () => {};

  export let onExternalAdd = (val) => {
    val.forEach((value) => {
      if (!value || items.some((item) => item[keyProperty] === value[keyProperty])) {
        return;
      }

      items = [...items, { [keyProperty]: value[keyProperty], [valueProperty]: value[valueProperty] }];
      errors = [...errors, [false, false]];
      refs = [...refs, [null, null]];
    });
  };

  let disableSubmit = true;
  let items = deepCopy(values);

  let errors = Array.from({ length: items.length }, () => [false, false]);
  let refs = Array.from({ length: items.length }, () => [null, null]);

  const onEdit = () => {
    editing = true;
    setTimeout(() => refs[0] && refs[0][0] && refs[0][0]?.focus(), 100);
  };

  const onCancel = () => {
    editing = false;
    items = deepCopy(values);
    errors = Array.from({ length: items.length }, () => [false, false]);
  };

  const onSave = () => {
    let error = false;

    if (items.length && (validation || keyValidation || valueValidation)) {
      items.forEach((item, index) => {
        if (keyValidation && !keyValidation(item[keyProperty])) {
          errors[index][0] = true;
          error = true;
        }

        if (valueValidation && !valueValidation(item[valueProperty])) {
          errors[index][1] = true;
          error = true;
        }
      });

      if (error) {
        return;
      }
    }

    values = [...items.filter(Boolean)];
    items = deepCopy(values);

    editing = false;

    if (onUpdate) {
      onUpdate(id, label, values);
    }
  };

  const onAdd = () => {
    items = [...items, { [keyProperty]: '', [valueProperty]: '' }];
    errors = [...errors, [false, false]];
    refs = [...refs, [null, null]];

    setTimeout(() => refs.length && refs.slice(-1)[0][0]?.focus(), 100);
  };

  const onRemove = (index) => {
    items.splice(index, 1);
    items = [...items];

    errors.splice(index, 1);
    errors = [...errors];

    const minIndex = Math.max(index - 1, 0);

    setTimeout(() => refs.length && refs[minIndex][0]?.focus(), 100);
  };

  const onKeyUp = (index, field) => {
    errors[index][field] = false;
  };

  $: disableSubmit = JSON.stringify(values) === JSON.stringify(items);
  $: hasErrors = errors.flat(2).filter(Boolean).length;
</script>

<Field>
  <FieldHeader>
    <FieldLabel {label} {tooltipIcon} {tooltip} bind:error={hasErrors} />
    <FieldActions bind:readonly bind:editing bind:disableSubmit {onSave} {onEdit} {onCancel} {showActions} />
  </FieldHeader>
  <FieldValue {showActions}>
    <div class="field-list">
      {#if editing}
        <slot name="edit" {onExternalAdd} />
        <ul>
          {#if items.length}
            <li class="headings">
              <span>{keyColumnLabel}</span>
              <span>{valueColumnLabel}</span>
              <i />
            </li>
          {/if}

          {#each items as item, index}
            <li class="item">
              <div class:read-only={keyReadOnly}>
                {#if keyReadOnly}
                  {@html formatKey(item[keyProperty] || '&nbsp;')}
                {:else}
                  <Input
                    type="text"
                    class={`raw ${errors[index][0] ? 'error' : ''}`}
                    bind:ref={refs[index][0]}
                    bind:value={item[keyProperty]}
                    on:keyup={() => onKeyUp(index, 0)}
                  />
                  {#if errors[index][0]}
                    <div class="error">{keyValidationError}</div>
                  {/if}
                {/if}
              </div>
              <div class:read-only={valueReadOnly}>
                {#if valueReadOnly}
                  {@html formatValue(item[valueProperty] || '&nbsp;')}
                {:else}
                  <Input
                    type="text"
                    class={`raw ${errors[index][1] ? 'error' : ''}`}
                    bind:ref={refs[index][1]}
                    bind:value={item[valueProperty]}
                    on:keyup={() => onKeyUp(index, 1)}
                  />
                {/if}
              </div>
              {#if !hideActionButtons}
                <Icon name="x-circle" role="button" tabindex="0" on:click={() => onRemove(index)} />
              {/if}
            </li>
            {#if errors[index][0] || errors[index][1]}
              <li class="error">
                <div class="error">{keyValidationError || ''}</div>
                <div class="error">{valueValidationError || ''}</div>
              </li>
            {/if}
          {/each}
        </ul>
        {#if !hideActionButtons}
          <div>
            <Button color="primary" on:click={onAdd}>
              {@html addButtonLabel}
            </Button>
            <slot name="action" {onExternalAdd} />
          </div>
        {/if}
      {:else if items.length}
        <div class="field-list">
          <ul>
            <li class="headings">
              <span>{keyColumnLabel}</span>
              <span>{valueColumnLabel}</span>
            </li>
            {#each items as item}
              <li class="item display">
                <div>{@html formatKey(item[keyProperty] || '&nbsp;')}</div>
                <div>{@html formatValue(item[valueProperty] || '&nbsp;')}</div>
              </li>
            {/each}
          </ul>
        </div>
      {:else}
        {emptyPlaceholder}
      {/if}
    </div>
  </FieldValue>
  {#if !editing}
    <slot name="preview" />
  {/if}
</Field>

<style lang="scss" src="./key-value.scss"></style>
